/**
 * CSV builders, ported from the string-building part of loader-export.js
 * (exportCSV and exportBudgetCSV). Pure: the download and the PDF writer are
 * not here (PDF export is deferred, see PORT_NOTES.md).
 */
import type { Antenna, Mission } from '@/features/loader/types';
import type { AnalysisResults, BudgetProjection } from './types';

export type CsvCell = string | number | null | undefined;

/** Rows to CSV text: cells with a comma, quote or newline are quoted, quotes doubled, rows joined by \n. */
export function toCsv(rows: CsvCell[][]): string {
  return rows
    .map((row) =>
      row
        .map((cell) => {
          let str = String(cell === undefined || cell === null ? '' : cell);
          if (str.indexOf(',') !== -1 || str.indexOf('"') !== -1 || str.indexOf('\n') !== -1) {
            str = '"' + str.replace(/"/g, '""') + '"';
          }
          return str;
        })
        .join(','),
    )
    .join('\n');
}

function byId<T extends { id: string }>(items: T[]): Map<string, T> {
  return new Map(items.map((item) => [item.id, item]));
}

/** Loading matrix: header, then one row per mission in priority order. */
export function matrixRows(results: AnalysisResults, missions: Mission[], antennas: Antenna[]): CsvCell[][] {
  const rows: CsvCell[][] = [];
  const header: CsvCell[] = ['Mission', 'Priority'];
  for (const a of antennas) header.push(a.shortName + ' (min)');
  header.push('Total Minutes', 'Total Passes', 'SLA %', 'SLA Status');
  rows.push(header);

  const sorted = missions.slice().sort((a, b) => a.priorityTier - b.priorityTier);
  for (const mission of sorted) {
    const row: CsvCell[] = [mission.shortName, 'P' + mission.priorityTier];
    const sla = results.missionSLA[mission.id];
    for (const antenna of antennas) {
      const perAntenna = sla?.perAntenna && sla.perAntenna[antenna.id];
      row.push(perAntenna ? perAntenna.minutes : 0);
    }
    row.push(sla?.totalMinutes || 0);
    row.push(sla?.passes || 0);
    row.push((sla?.percentage || 0) + '%');
    row.push(sla?.status || 'N/A');
    rows.push(row);
  }
  return rows;
}

/** 'Utilization' row: percentage per antenna, '--' when the antenna has no entry. */
export function utilisationRow(results: AnalysisResults, antennas: Antenna[]): CsvCell[] {
  const row: CsvCell[] = ['Utilization', ''];
  for (const antenna of antennas) {
    const util = results.antennaUtil[antenna.id];
    row.push(util ? util.percentage + '%' : '--');
  }
  return row;
}

/** CONFLICTS section: title, header and one row per conflict with its resolution. */
export function conflictRows(
  results: AnalysisResults,
  missions: Mission[],
  antennas: Antenna[],
): CsvCell[][] {
  const missionById = byId(missions);
  const antennaById = byId(antennas);
  const rows: CsvCell[][] = [
    ['CONFLICTS'],
    ['ID', 'Mission A', 'Mission B', 'Antenna', 'Resolution', 'Data Capture'],
  ];
  results.conflicts.forEach((conflict, idx) => {
    const resolution = results.resolutions[idx];
    rows.push([
      conflict.id,
      missionById.get(conflict.missionA)?.shortName ?? conflict.missionA,
      missionById.get(conflict.missionB)?.shortName ?? conflict.missionB,
      antennaById.get(conflict.antennaId)?.shortName ?? conflict.antennaId,
      resolution?.strategy || 'UNRESOLVED',
      Math.round((resolution?.dataCapture || 0) * 100) + '%',
    ]);
  });
  return rows;
}

/** CONTACT WINDOWS section: every pass in results.contactWindows, relay windows included (TDRS ids as names). */
export function contactWindowRows(
  results: AnalysisResults,
  missions: Mission[],
  antennas: Antenna[],
): CsvCell[][] {
  const missionById = byId(missions);
  const antennaById = byId(antennas);
  const rows: CsvCell[][] = [
    ['CONTACT WINDOWS (GANTT TIMELINE)'],
    ['Mission', 'Antenna', 'Station', 'Start (UTC)', 'End (UTC)', 'Duration (min)', 'Max Elevation (°)'],
  ];
  const contactWindows = results.contactWindows || {};
  for (const missionId of Object.keys(contactWindows)) {
    const missionContacts = contactWindows[missionId];
    const missionName = missionById.get(missionId)?.shortName ?? missionId;
    for (const antennaId of Object.keys(missionContacts)) {
      const antenna = antennaById.get(antennaId);
      const antennaName = antenna ? antenna.shortName : antennaId;
      const stationName = antenna ? (antenna.location.name ?? '') : '';
      for (const pass of missionContacts[antennaId]) {
        rows.push([
          missionName,
          antennaName,
          stationName,
          new Date(pass.startMs).toISOString(),
          new Date(pass.endMs).toISOString(),
          Math.round(pass.durationMin * 10) / 10,
          Math.round(pass.maxElevation || 0),
        ]);
      }
    }
  }
  return rows;
}

/** SUMMARY section. */
export function summaryRows(results: AnalysisResults): CsvCell[][] {
  const s = results.summary;
  return [
    ['SUMMARY'],
    ['Overall SLA', s.overallSLA + '%'],
    ['Total Missions', s.missionCount],
    ['Total Antennas', s.antennaCount],
    ['Conflicts', s.totalConflicts],
    ['Resolved', s.resolvedConflicts],
    ['Unresolved', s.unresolvedConflicts],
    ['Analysis Time', new Date(s.analysisTimeMs).toISOString()],
  ];
}

/** The full analysis export: matrix, utilisation, conflicts, contact windows and summary, blank-row separated. */
export function buildAnalysisCsv(results: AnalysisResults, missions: Mission[], antennas: Antenna[]): string {
  const rows: CsvCell[][] = [
    ...matrixRows(results, missions, antennas),
    [],
    utilisationRow(results, antennas),
    [],
    ...conflictRows(results, missions, antennas),
    [],
    ...contactWindowRows(results, missions, antennas),
    [],
    ...summaryRows(results),
  ];
  return toCsv(rows);
}

/** Budget projection rows: header with one Util% column per antenna of the first row, then quarters and recommendations. */
export function budgetRows(projection: BudgetProjection, antennas: Antenna[]): CsvCell[][] {
  const antennaById = byId(antennas);
  const results = projection.yearlyResults;
  const rows: CsvCell[][] = [];

  const header: CsvCell[] = ['Quarter', 'SLA %', 'Conflicts', 'Mission Count'];
  if (results.length > 0) {
    for (const id of Object.keys(results[0].utilization || {})) {
      const a = antennaById.get(id);
      header.push(a ? a.shortName + ' Util%' : id);
    }
  }
  rows.push(header);

  for (const r of results) {
    const row: CsvCell[] = [r.label, r.sla + '%', r.conflicts, r.missionCount];
    for (const id of Object.keys(r.utilization || {})) row.push(r.utilization[id] + '%');
    rows.push(row);
  }

  rows.push([]);
  rows.push(['RECOMMENDATIONS']);
  for (const rec of projection.recommendations) rows.push([rec]);
  return rows;
}

/** The budget projection export. */
export function buildBudgetCsv(projection: BudgetProjection, antennas: Antenna[]): string {
  return toCsv(budgetRows(projection, antennas));
}
